import { useState } from 'react';
import { cn } from '../../lib/utils';
import { Dialog, DialogContent, DialogTitle } from '../../components/ui/dialog';
import {
  ScanLine, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle, CalendarDays,
  DoorOpen, Eye, Trash2, X, FileText, Image, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import DocumentUploadFlow from '../../components/shared/DocumentUploadFlow';
import { useAppSync } from '../../contexts/AppSyncContext';
import { useAppMode } from '../../contexts/AppModeContext';

const today = new Date();
const DAY_LABEL = today.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

const INITIAL_PATIENTS = [
  {
    id: 'MAP-001', name: 'John Smith', initials: 'JS', time: '9:00 AM', type: 'Follow-up', chief: 'Diabetes management',
    tasks: [
      { key: 'intake', label: 'Intake forms', done: true, type: 'lens' },
      { key: 'vitals', label: 'Vitals', done: true, type: 'lens' },
      { key: 'prior_note', label: 'Prior visit note', done: false, type: 'lens' },
      { key: 'lab_results', label: 'Lab results', done: false, type: 'lens' },
    ],
    roomReady: false,
    docs: [
      { id: 'd1', name: 'Lab Report (HbA1c)', type: 'Lab result', time: '8:15 AM', preview: null, extracted: [{ name: 'Test', value: 'HbA1c', confidence: 98 }, { name: 'Result', value: '6.8%', confidence: 96 }, { name: 'Date', value: '03/26/2026', confidence: 94 }] },
      { id: 'd2', name: 'Previous Visit Note', type: 'Clinical doc', time: '8:10 AM', preview: null, extracted: [{ name: 'Provider', value: 'Dr. Sarah Chen', confidence: 99 }, { name: 'Visit Date', value: '01/15/2026', confidence: 97 }, { name: 'Diagnosis', value: 'Type 2 DM', confidence: 92 }] },
    ],
    flagged: false, flagReason: '',
  },
  {
    id: 'MAP-002', name: 'Maria Garcia', initials: 'MG', time: '9:30 AM', type: 'New Patient', chief: 'Annual physical',
    tasks: [
      { key: 'intake', label: 'Intake forms', done: true, type: 'lens' },
      { key: 'vitals', label: 'Vitals', done: false, type: 'lens', blocked: true, blockReason: 'enter now' },
      { key: 'new_patient', label: 'New patient forms', done: false, type: 'lens' },
      { key: 'insurance', label: 'Insurance card', done: false, type: 'lens' },
    ],
    roomReady: false,
    docs: [{ id: 'd3', name: 'Patient Intake Form', type: 'Intake form', time: '8:45 AM', preview: null, extracted: [{ name: 'Name', value: 'Maria Garcia', confidence: 99 }, { name: 'DOB', value: '07/22/1988', confidence: 95 }, { name: 'Allergies', value: 'Penicillin', confidence: 88 }] }],
    flagged: true, flagReason: 'vitals not yet entered',
  },
  {
    id: 'MAP-003', name: 'Robert Johnson', initials: 'RJ', time: '10:00 AM', type: 'Sick Visit', chief: 'Persistent cough',
    tasks: [
      { key: 'intake', label: 'Intake forms', done: false, type: 'lens', blocked: true, blockReason: 'missing' },
      { key: 'vitals', label: 'Vitals', done: false, type: 'lens' },
      { key: 'prior_note', label: 'Prior visit note', done: false, type: 'lens' },
      { key: 'referral', label: 'Referral doc', done: false, type: 'lens' },
    ],
    roomReady: false,
    docs: [],
    flagged: true, flagReason: 'intake forms missing',
  },
  {
    id: 'MAP-004', name: 'Emily Davis', initials: 'ED', time: '10:30 AM', type: 'Follow-up', chief: 'Cardiology referral',
    tasks: [
      { key: 'intake', label: 'Intake forms', done: true, type: 'lens' },
      { key: 'vitals', label: 'Vitals', done: true, type: 'lens' },
      { key: 'prior_note', label: 'Prior visit note', done: true, type: 'lens' },
      { key: 'clinical_docs', label: 'Clinical docs', done: true, type: 'lens' },
    ],
    roomReady: true,
    docs: [
      { id: 'd4', name: 'Referral Letter', type: 'Referral', time: '7:50 AM', preview: null, extracted: [{ name: 'From', value: 'Dr. Patel', confidence: 97 }, { name: 'To', value: 'Dr. Chen', confidence: 98 }, { name: 'Reason', value: 'Cardiology eval', confidence: 95 }] },
      { id: 'd5', name: 'EKG Report', type: 'Lab result', time: '7:55 AM', preview: null, extracted: [{ name: 'Result', value: 'Normal sinus rhythm', confidence: 93 }, { name: 'HR', value: '72 bpm', confidence: 99 }] },
      { id: 'd6', name: 'Insurance Verification', type: 'Insurance card', time: '8:00 AM', preview: null, extracted: [{ name: 'Member ID', value: 'BCBS-456789', confidence: 96 }, { name: 'Copay', value: '$30', confidence: 91 }] },
    ],
    flagged: false, flagReason: '',
  },
  {
    id: 'MAP-005', name: 'Sarah Williams', initials: 'SW', time: '11:00 AM', type: 'Follow-up', chief: 'HTN med check',
    tasks: [
      { key: 'intake', label: 'Intake forms', done: false, type: 'lens' },
      { key: 'vitals', label: 'Vitals', done: false, type: 'lens' },
      { key: 'prior_note', label: 'Prior visit note', done: false, type: 'lens' },
      { key: 'schedule', label: 'Patient schedule', done: false, type: 'lens' },
    ],
    roomReady: false,
    docs: [],
    flagged: false, flagReason: '',
  },
];

const getStatus = (patient) => {
  const doneCount = patient.tasks.filter(t => t.done).length;
  if (doneCount === patient.tasks.length && patient.roomReady) return 'ready';
  if (patient.flagged) return 'flagged';
  if (doneCount > 0) return 'in-progress';
  return 'not-started';
};

const getBorderColor = (status) => ({
  'ready': '#bbf7d0', 'flagged': '#fde68a', 'in-progress': '#e8e8e8', 'not-started': '#e8e8e8',
}[status]);

const getBadge = (status, patient) => {
  const blocked = patient.tasks.find(t => t.blocked);
  if (status === 'ready') return { label: 'Ready', bg: '#f0fdf4', color: '#16a34a' };
  if (status === 'flagged' && blocked) return { label: `${blocked.label} missing`, bg: '#fef2f2', color: '#991b1b' };
  if (status === 'in-progress') return { label: 'In progress', bg: '#fffbeb', color: '#92400e' };
  return { label: 'Not started', bg: '#f5f5f5', color: '#888888' };
};

// Mock extracted data for Lens simulation
const DEMO_EXTRACTIONS = {
  'Intake forms': [{ name: 'Name', value: 'Patient Name', confidence: 99 }, { name: 'DOB', value: '01/15/1970', confidence: 97 }, { name: 'Allergies', value: 'None', confidence: 90 }, { name: 'Medications', value: 'Metformin 500mg', confidence: 85 }],
  'Vitals': [{ name: 'BP', value: '120/80 mmHg', confidence: 96 }, { name: 'HR', value: '76 bpm', confidence: 98 }, { name: 'Temp', value: '98.6F', confidence: 97 }, { name: 'Weight', value: '180 lbs', confidence: 94 }],
  'Prior visit note': [{ name: 'Provider', value: 'Dr. Chen', confidence: 98 }, { name: 'Date', value: '02/15/2026', confidence: 96 }, { name: 'Assessment', value: 'DM well controlled', confidence: 88 }],
  'Lab results': [{ name: 'Test', value: 'CBC', confidence: 97 }, { name: 'WBC', value: '7.2 K/uL', confidence: 95 }, { name: 'Hemoglobin', value: '14.2 g/dL', confidence: 96 }],
  'Insurance card': [{ name: 'Member ID', value: 'BCB-123456', confidence: 94 }, { name: 'Group', value: 'GRP-789', confidence: 91 }, { name: 'Copay', value: '$25', confidence: 88 }],
  'New patient forms': [{ name: 'Name', value: 'Patient Name', confidence: 99 }, { name: 'Emergency Contact', value: 'Jane Doe', confidence: 95 }, { name: 'Primary Care', value: 'Dr. Chen', confidence: 92 }],
  'Referral doc': [{ name: 'From', value: 'Dr. Patel', confidence: 97 }, { name: 'Reason', value: 'Specialist evaluation', confidence: 93 }, { name: 'Date', value: '03/20/2026', confidence: 96 }],
  'Clinical docs': [{ name: 'Type', value: 'Progress Note', confidence: 95 }, { name: 'Date', value: '03/15/2026', confidence: 97 }, { name: 'Summary', value: 'Follow-up visit', confidence: 89 }],
  'Patient schedule': [{ name: 'Next Appt', value: '04/15/2026', confidence: 96 }, { name: 'Provider', value: 'Dr. Chen', confidence: 98 }, { name: 'Type', value: 'Follow-up', confidence: 94 }],
};

// Shared patient card component used by both MA Dashboard and Solo Doctor Mode
export const PatientCardList = ({ patients, setPatients, expandedCard, setExpandedCard, cardTab, setCardTab, syncActions }) => {
  const [uploadFlow, setUploadFlow] = useState(null);
  const [uploadTaskType, setUploadTaskType] = useState(null);
  const [uploadTaskKey, setUploadTaskKey] = useState(null);
  const [viewingDoc, setViewingDoc] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [roomingPatient, setRoomingPatient] = useState(null);
  const [roomPref, setRoomPref] = useState(() => localStorage.getItem('room-pref') || 'lumina');

  const ROOMS = ['Exam 1', 'Exam 2', 'Exam 3', 'Exam 4', 'Procedure Room', 'Triage'];

  const getActiveTab = (id) => cardTab[id] || 'prep';

  // Open the Lens upload lightbox for a specific task
  const handleOpenLensForTask = (patient, taskKey, taskLabel) => {
    setUploadFlow(patient);
    setUploadTaskType(taskLabel);
    setUploadTaskKey(taskKey);
  };

  const handleRoomPatient = (patientId, room) => {
    setPatients(prev => prev.map(p => p.id === patientId ? { ...p, roomReady: true, assignedRoom: room } : p));
    setRoomingPatient(null);
    // Cross-app sync: notify Echo
    if (syncActions?.roomPatient) {
      syncActions.roomPatient(patientId, room, 'lumina');
    } else {
      toast.success(`Patient roomed in ${room}`);
    }
  };

  const handleDeleteDoc = (patientId, docId, docType) => {
    setPatients(prev => prev.map(p => {
      if (p.id !== patientId) return p;
      const taskKey = p.tasks.find(t => t.label === docType)?.key;
      return {
        ...p,
        docs: p.docs.filter(d => d.id !== docId),
        tasks: taskKey ? p.tasks.map(t => t.key === taskKey ? { ...t, done: false } : t) : p.tasks,
      };
    }));
    setDeleteConfirm(null);
    toast.success('Document removed');
  };

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }} data-testid="ma-patient-list">
        {patients.map(patient => {
          const status = getStatus(patient);
          const border = getBorderColor(status);
          const badge = getBadge(status, patient);
          const isExpanded = expandedCard === patient.id;
          const doneCount = patient.tasks.filter(t => t.done).length;
          const totalCount = patient.tasks.length;
          const activeTab = getActiveTab(patient.id);
          const allTasksDone = doneCount === totalCount;

          return (
            <div key={patient.id}
              style={{ background: '#ffffff', border: `0.5px solid ${border}`, borderRadius: 12, overflow: 'hidden', opacity: status === 'not-started' ? 0.8 : 1, transition: 'opacity 150ms' }}
              data-testid={`ma-patient-${patient.id}`}>

              {/* Header Row */}
              <div
                className="flex items-center cursor-pointer"
                style={{ padding: '12px 16px', gap: 12 }}
                onClick={() => setExpandedCard(isExpanded ? null : patient.id)}
                data-testid={`patient-header-${patient.id}`}
              >
                <div style={{ width: 52, flexShrink: 0, textAlign: 'center' }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: 0, lineHeight: 1.2 }}>{patient.time}</p>
                  <p style={{ fontSize: 10, color: '#aaaaaa', margin: '1px 0 0' }}>{patient.type}</p>
                </div>
                <div className="flex items-center justify-center flex-shrink-0" style={{ width: 32, height: 32, borderRadius: '50%', background: '#f0f0f0', fontSize: 11, fontWeight: 500, color: '#555555' }}>
                  {patient.initials}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: 0 }}>{patient.name}</p>
                  <p style={{ fontSize: 11, color: '#888888', margin: '1px 0 0' }}>{patient.chief}</p>
                </div>
                <div className="flex items-center flex-shrink-0" style={{ gap: 8 }}>
                  {/* Progress dots */}
                  <div className="flex items-center" style={{ gap: 3 }}>
                    {patient.tasks.map((t, i) => (
                      <div key={i} style={{
                        width: 8, height: 8, borderRadius: '50%',
                        background: t.done ? '#22c55e' : t.blocked ? '#d97706' : '#6b5ce7',
                      }} />
                    ))}
                  </div>
                  <span style={{ fontSize: 11, color: '#aaaaaa' }}>{doneCount}/{totalCount}</span>
                  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: badge.bg, color: badge.color }}>{badge.label}</span>

                  {/* Room Patient (Echo) button — always visible next to Lens */}
                  {!patient.roomReady && (
                    <button
                      onClick={e => { e.stopPropagation(); setRoomingPatient(patient); }}
                      className="flex items-center"
                      style={{ background: '#ffffff', color: '#555555', borderRadius: 6, height: 28, padding: '0 10px', fontSize: 11, fontWeight: 500, border: '1.5px solid #6b5ce7', cursor: 'pointer', gap: 4, transition: 'border-color 150ms, background 150ms' }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f5f3ff'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#ffffff'; }}
                      data-testid={`room-btn-${patient.id}`}
                    >
                      <DoorOpen style={{ width: 12, height: 12, color: '#6b5ce7' }} /> Room (Echo)
                    </button>
                  )}

                  {/* Lens button */}
                  {status === 'ready' ? (
                    <CheckCircle2 style={{ width: 18, height: 18, color: '#22c55e' }} />
                  ) : patient.tasks.some(t => !t.done) ? (
                    <button
                      onClick={e => { e.stopPropagation(); setUploadFlow(patient); setUploadTaskType(null); setUploadTaskKey(null); }}
                      className="flex items-center"
                      style={{ background: '#6b5ce7', color: '#ffffff', borderRadius: 6, height: 28, padding: '0 10px', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', gap: 4 }}
                      data-testid={`lens-btn-${patient.id}`}
                    >
                      <ScanLine style={{ width: 12, height: 12 }} /> Lens
                    </button>
                  ) : null}

                  {isExpanded ? <ChevronUp style={{ width: 14, height: 14, color: '#aaaaaa' }} /> : <ChevronDown style={{ width: 14, height: 14, color: '#aaaaaa' }} />}
                </div>
              </div>

              {/* Expanded Section */}
              {isExpanded && (
                <div style={{ borderTop: '0.5px solid #f0f0f0' }}>
                  {/* Tabs */}
                  <div className="flex items-center" style={{ padding: '0 16px', gap: 16 }}>
                    {['prep', 'docs'].map(tab => (
                      <button key={tab}
                        onClick={() => setCardTab(prev => ({ ...prev, [patient.id]: tab }))}
                        style={{
                          padding: '10px 0', fontSize: 11, fontWeight: 500, cursor: 'pointer', background: 'none', border: 'none',
                          borderBottom: activeTab === tab ? '2px solid #6b5ce7' : '2px solid transparent',
                          color: activeTab === tab ? '#111111' : '#aaaaaa',
                        }}
                        data-testid={`tab-${tab}-${patient.id}`}
                      >
                        {tab === 'prep' ? 'Pre-Visit Prep' : `Uploaded docs (${patient.docs.length})`}
                      </button>
                    ))}
                  </div>

                  {activeTab === 'prep' ? (
                    <div style={{ padding: '0 16px 14px' }}>
                      <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', paddingTop: 10, marginBottom: 8 }}>Pre-Visit Prep</p>
                      <div className="flex flex-wrap" style={{ gap: 6 }}>
                        {patient.tasks.map(task => {
                          const isDone = task.done;
                          const isBlocked = task.blocked;

                          let pillStyle = {};
                          let label = task.label;

                          if (isDone) {
                            pillStyle = { background: '#f0fdf4', color: '#15803d', border: '0.5px solid #bbf7d0' };
                          } else if (isBlocked) {
                            pillStyle = { background: '#fffbeb', color: '#92400e', border: '0.5px solid #fde68a' };
                            label = `${task.label} — ${task.blockReason}`;
                          } else {
                            pillStyle = { background: '#f5f3ff', color: '#5b21b6', border: '0.5px solid #ddd6fe' };
                          }

                          return (
                            <button
                              key={task.key}
                              disabled={isDone}
                              onClick={() => handleOpenLensForTask(patient, task.key, task.label)}
                              className="flex items-center"
                              style={{ ...pillStyle, borderRadius: 20, height: 28, padding: '0 10px', fontSize: 11, fontWeight: 500, cursor: isDone ? 'default' : 'pointer', gap: 5, transition: 'border-color 150ms' }}
                              data-testid={`task-${patient.id}-${task.key}`}
                            >
                              {isDone ? (
                                <CheckCircle2 style={{ width: 12, height: 12, color: '#15803d' }} />
                              ) : isBlocked ? (
                                <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
                              ) : (
                                <ScanLine style={{ width: 12, height: 12, color: '#5b21b6' }} />
                              )}
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ) : (
                    /* Uploaded Docs Tab */
                    <div style={{ padding: '12px 16px 14px' }}>
                      {patient.docs.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '20px 0' }}>
                          <ScanLine style={{ width: 20, height: 20, color: '#cccccc', margin: '0 auto 6px' }} />
                          <p style={{ fontSize: 12, color: '#aaaaaa', margin: '0 0 6px' }}>No documents uploaded yet</p>
                          <button
                            onClick={() => { setUploadFlow(patient); setUploadTaskType(null); setUploadTaskKey(null); }}
                            style={{ fontSize: 12, color: '#6b5ce7', background: 'none', border: 'none', cursor: 'pointer' }}
                          >Scan with Lens →</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                          {patient.docs.map((doc) => (
                            <div key={doc.id}>
                              <div className="flex items-center" style={{ gap: 10, padding: '6px 0' }}>
                                <div className="flex items-center justify-center" style={{ width: 28, height: 28, borderRadius: 6, background: '#f0f0f0', flexShrink: 0 }}>
                                  {doc.type === 'Lab result' || doc.type === 'Clinical doc' ? <FileText style={{ width: 14, height: 14, color: '#888' }} /> : <Image style={{ width: 14, height: 14, color: '#888' }} />}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p style={{ fontSize: 12, fontWeight: 500, color: '#111111', margin: 0 }}>{doc.name}</p>
                                  <p style={{ fontSize: 10, color: '#aaaaaa', margin: '1px 0 0' }}>{doc.type} · {doc.time}</p>
                                </div>
                                <div className="flex items-center" style={{ gap: 4 }}>
                                  <button
                                    onClick={() => setViewingDoc({ ...doc, patientName: patient.name })}
                                    style={{ width: 28, height: 28, borderRadius: 6, background: '#f5f5f5', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    title="View" data-testid={`view-doc-${doc.id}`}
                                  >
                                    <Eye style={{ width: 13, height: 13, color: '#888' }} />
                                  </button>
                                  <button
                                    onClick={() => {
                                      // Replace: delete old doc then open upload for same task type
                                      const taskKey = patient.tasks.find(t => t.label === doc.type)?.key;
                                      handleDeleteDoc(patient.id, doc.id, doc.type);
                                      setUploadFlow(patient);
                                      setUploadTaskType(doc.type);
                                      setUploadTaskKey(taskKey || null);
                                    }}
                                    style={{ width: 28, height: 28, borderRadius: 6, background: '#f5f5f5', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    title="Replace" data-testid={`replace-doc-${doc.id}`}
                                  >
                                    <RefreshCw style={{ width: 13, height: 13, color: '#6b5ce7' }} />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ patientId: patient.id, docId: doc.id, docName: doc.name, docType: doc.type })}
                                    style={{ width: 28, height: 28, borderRadius: 6, background: '#f5f5f5', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                                    title="Delete" data-testid={`delete-doc-${doc.id}`}
                                  >
                                    <Trash2 style={{ width: 13, height: 13, color: '#888' }} />
                                  </button>
                                </div>
                              </div>
                              {/* Inline delete confirmation */}
                              {deleteConfirm?.docId === doc.id && (
                                <div style={{ padding: '8px 0 4px 38px' }}>
                                  <p style={{ fontSize: 11, color: '#555', margin: '0 0 6px' }}>Remove "{doc.name}"? The corresponding task will be marked incomplete.</p>
                                  <div className="flex" style={{ gap: 6 }}>
                                    <button onClick={() => handleDeleteDoc(patient.id, doc.id, doc.type)} style={{ background: '#ef4444', color: '#fff', height: 26, borderRadius: 6, padding: '0 10px', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer' }} data-testid={`confirm-delete-${doc.id}`}>Yes, remove</button>
                                    <button onClick={() => setDeleteConfirm(null)} style={{ background: '#fff', color: '#555', height: 26, borderRadius: 6, padding: '0 10px', fontSize: 11, border: '0.5px solid #e0e0e0', cursor: 'pointer' }}>Cancel</button>
                                  </div>
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Document View Lightbox */}
      <Dialog open={!!viewingDoc} onOpenChange={v => { if (!v) setViewingDoc(null); }}>
        <DialogContent className="max-w-md p-0 gap-0 overflow-hidden" aria-describedby={undefined} data-testid="doc-view-lightbox">
          {viewingDoc && (
            <>
              <div className="flex items-center justify-between" style={{ padding: '14px 20px', borderBottom: '0.5px solid #e8e8e8' }}>
                <div>
                  <DialogTitle style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: 0 }}>{viewingDoc.name}</DialogTitle>
                  <p style={{ fontSize: 11, color: '#aaaaaa', marginTop: 2 }}>{viewingDoc.type} · {viewingDoc.patientName} · {viewingDoc.time}</p>
                </div>
              </div>
              {/* Document Preview */}
              <div style={{ background: '#f5f5f5', padding: 24, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
                <div style={{ textAlign: 'center' }}>
                  <FileText style={{ width: 40, height: 40, color: '#cccccc', margin: '0 auto 8px' }} />
                  <p style={{ fontSize: 13, fontWeight: 500, color: '#555' }}>{viewingDoc.name}</p>
                  <p style={{ fontSize: 11, color: '#aaaaaa' }}>Scanned document preview</p>
                </div>
              </div>
              {/* Extracted Data */}
              <div style={{ padding: '16px 20px' }}>
                <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Extracted Data (Lens)</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {viewingDoc.extracted?.map((field, i) => (
                    <div key={i} className="flex items-center justify-between" style={{ padding: '6px 10px', borderRadius: 6, background: '#fafafa' }}>
                      <span style={{ fontSize: 11, color: '#888888' }}>{field.name}</span>
                      <div className="flex items-center" style={{ gap: 6 }}>
                        <span style={{ fontSize: 12, fontWeight: 500, color: '#111111' }}>{field.value}</span>
                        <span style={{
                          fontSize: 9, padding: '1px 5px', borderRadius: 10, fontWeight: 500,
                          background: field.confidence >= 95 ? '#f0fdf4' : field.confidence >= 80 ? '#fffbeb' : '#fef2f2',
                          color: field.confidence >= 95 ? '#166534' : field.confidence >= 80 ? '#92400e' : '#991b1b',
                        }}>{field.confidence}%</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ padding: '0 20px 16px' }}>
                <button onClick={() => setViewingDoc(null)} style={{ width: '100%', height: 36, borderRadius: 8, background: '#111111', color: '#ffffff', fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}>Close</button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Document Upload Flow */}
      <DocumentUploadFlow
        open={!!uploadFlow}
        onOpenChange={v => { if (!v) { setUploadFlow(null); setUploadTaskType(null); setUploadTaskKey(null); } }}
        patientName={uploadFlow?.name || 'Patient'}
        patients={patients}
        mode="single"
        taskType={uploadTaskType}
        onComplete={(docs) => {
          if (uploadFlow?.id) {
            setPatients(prev => prev.map(p => {
              if (p.id !== uploadFlow.id) return p;
              const newDocs = docs.map(d => ({
                id: `d-${Date.now()}-${Math.random().toString(36).slice(2,5)}`,
                name: d.name, type: d.docType || uploadTaskType || 'Document',
                time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
                preview: d.preview, extracted: d.extractedFields || [],
              }));
              // Mark the task as done if we have a task key
              const updatedTasks = uploadTaskKey
                ? p.tasks.map(t => t.key === uploadTaskKey ? { ...t, done: true, blocked: false, blockReason: '' } : t)
                : p.tasks;
              return { ...p, docs: [...p.docs, ...newDocs], tasks: updatedTasks };
            }));
          }
          toast.success(`${docs.length} document(s) uploaded via Lens`);
          setUploadFlow(null);
          setUploadTaskType(null);
          setUploadTaskKey(null);
        }}
      />

      {/* Echo Room Patient Lightbox */}
      <Dialog open={!!roomingPatient} onOpenChange={v => { if (!v) setRoomingPatient(null); }}>
        <DialogContent className="max-w-sm p-0 gap-0 overflow-hidden" aria-describedby={undefined} data-testid="room-patient-dialog">
          {roomingPatient && (
            <>
              <div style={{ padding: '18px 20px 14px', borderBottom: '0.5px solid #e8e8e8' }}>
                <DialogTitle style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: 0 }}>Room Patient</DialogTitle>
                <p style={{ fontSize: 12, color: '#888888', marginTop: 4 }}>
                  Rooming <span style={{ fontWeight: 500, color: '#111111' }}>{roomingPatient.name}</span> — select a room below
                </p>
              </div>
              <div style={{ padding: '14px 20px' }}>
                <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Available rooms</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {ROOMS.map(room => (
                    <button
                      key={room}
                      onClick={() => handleRoomPatient(roomingPatient.id, room)}
                      className="flex items-center justify-between"
                      style={{ padding: '10px 12px', borderRadius: 8, border: '0.5px solid #e8e8e8', background: '#ffffff', cursor: 'pointer', transition: 'border-color 150ms, background 150ms' }}
                      onMouseEnter={e => { e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = '#f8faff'; }}
                      onMouseLeave={e => { e.currentTarget.style.borderColor = '#e8e8e8'; e.currentTarget.style.background = '#ffffff'; }}
                      data-testid={`room-select-${room.toLowerCase().replace(/\s/g, '-')}`}
                    >
                      <div className="flex items-center" style={{ gap: 8 }}>
                        <DoorOpen style={{ width: 14, height: 14, color: '#3b82f6' }} />
                        <span style={{ fontSize: 13, fontWeight: 500, color: '#111111' }}>{room}</span>
                      </div>
                      <span style={{ fontSize: 11, color: '#aaaaaa' }}>Available</span>
                    </button>
                  ))}
                </div>

                {/* Divider */}
                <div style={{ height: 0.5, background: '#f0f0f0', margin: '14px 0' }} />

                {/* Workflow preference */}
                <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>Workflow preference</p>
                <div className="flex" style={{ gap: 6 }}>
                  <button
                    onClick={() => { setRoomPref('lumina'); localStorage.setItem('room-pref', 'lumina'); }}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      border: roomPref === 'lumina' ? '1.5px solid #6b5ce7' : '0.5px solid #e0e0e0',
                      background: roomPref === 'lumina' ? '#f5f3ff' : '#ffffff',
                      color: roomPref === 'lumina' ? '#5b21b6' : '#555555',
                    }}
                    data-testid="pref-lumina"
                  >
                    Quick room (Lumina)
                  </button>
                  <button
                    onClick={() => { setRoomPref('echo'); localStorage.setItem('room-pref', 'echo'); }}
                    style={{
                      flex: 1, padding: '8px 10px', borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: 'pointer',
                      border: roomPref === 'echo' ? '1.5px solid #3b82f6' : '0.5px solid #e0e0e0',
                      background: roomPref === 'echo' ? '#eff6ff' : '#ffffff',
                      color: roomPref === 'echo' ? '#1d4ed8' : '#555555',
                    }}
                    data-testid="pref-echo"
                  >
                    Open Echo
                  </button>
                </div>
                <p style={{ fontSize: 10, color: '#aaaaaa', marginTop: 6 }}>
                  {roomPref === 'lumina' ? 'Room quickly and stay in Lumina' : 'Switch to Echo for full patient flow management'}
                  <span style={{ color: '#888', marginLeft: 4 }}>· Saved for future use</span>
                </p>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

const LuminaMADashboard = () => {
  const { isDemo } = useAppMode();
  const [patients, setPatients] = useState(INITIAL_PATIENTS);
  const [expandedCard, setExpandedCard] = useState('MAP-001');
  const [cardTab, setCardTab] = useState({});
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchUploadPatient, setBatchUploadPatient] = useState(null);
  const [batchUploadTaskKey, setBatchUploadTaskKey] = useState(null);
  const [batchUploadTaskType, setBatchUploadTaskType] = useState(null);

  // Cross-app sync
  const appSync = useAppSync();

  const readyCount = patients.filter(p => getStatus(p) === 'ready').length;
  const inProgressCount = patients.filter(p => getStatus(p) === 'in-progress').length;
  const notStartedCount = patients.filter(p => getStatus(p) === 'not-started').length;
  const flaggedCount = patients.filter(p => getStatus(p) === 'flagged').length;
  const flaggedPatients = patients.filter(p => p.flagged);

  // Batch upload helpers
  const patientsWithIncompleteTasks = patients.filter(p => p.tasks.some(t => !t.done));
  const totalIncompleteTasks = patientsWithIncompleteTasks.reduce((sum, p) => sum + p.tasks.filter(t => !t.done).length, 0);
  const totalTasks = patients.reduce((sum, p) => sum + p.tasks.length, 0);
  const totalCompleteTasks = totalTasks - totalIncompleteTasks;
  const batchProgress = totalTasks > 0 ? Math.round((totalCompleteTasks / totalTasks) * 100) : 100;

  const handleBatchStartPatient = (patient) => {
    const firstIncomplete = patient.tasks.find(t => !t.done);
    if (firstIncomplete) {
      setBatchUploadPatient(patient);
      setBatchUploadTaskKey(firstIncomplete.key);
      setBatchUploadTaskType(firstIncomplete.label);
    }
  };

  const handleBatchDocComplete = (docs) => {
    if (batchUploadPatient?.id) {
      setPatients(prev => prev.map(p => {
        if (p.id !== batchUploadPatient.id) return p;
        const newDocs = docs.map(d => ({
          id: `d-${Date.now()}-${Math.random().toString(36).slice(2, 5)}`,
          name: d.name, type: d.docType || batchUploadTaskType || 'Document',
          time: new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          preview: d.preview, extracted: d.extractedFields || [],
        }));
        const updatedTasks = batchUploadTaskKey
          ? p.tasks.map(t => t.key === batchUploadTaskKey ? { ...t, done: true, blocked: false, blockReason: '' } : t)
          : p.tasks;
        return { ...p, docs: [...p.docs, ...newDocs], tasks: updatedTasks };
      }));
    }
    toast.success(`Scanned: ${batchUploadTaskType}`);
    setBatchUploadPatient(null);
    setBatchUploadTaskKey(null);
    setBatchUploadTaskType(null);
  };

  return (
    <div data-testid="ma-dashboard">
      {/* Page Header */}
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 20, fontWeight: 500, color: '#111111', margin: 0 }}>Today's patients</h1>
        <p style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>Pre-visit prep · {DAY_LABEL} · {patients.length} patients</p>
      </div>

      {/* ── AI Proactive Alert (Principle 1 \u2014 MA) ── */}
      <div
        style={{ background: '#111111', borderRadius: 10, padding: '14px 18px', border: '0.5px solid #2a2a2a', marginBottom: 16 }}
        data-testid="ma-ai-proactive-alert"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="flex-shrink-0 mt-0.5" style={{ width: 14, height: 14, color: '#6b5ce7' }} />
          {isDemo ? (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.85)', lineHeight: 1.6, margin: 0 }}>
              Based on your schedule, <span style={{ fontWeight: 500, color: '#ffffff' }}>Robert Johnson</span> has no intake forms on file from a prior visit.{' '}
              <span style={{ fontWeight: 500, color: '#ffffff' }}>Maria Garcia</span> is a new patient \u2014 insurance verification will be needed.
            </p>
          ) : (
            <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6, margin: 0 }}>
              Proactive alerts will populate once the prep-status API is wired.
            </p>
          )}
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4" style={{ gap: 8, marginBottom: 16 }} data-testid="ma-stats-row">
        {[
          { label: 'Ready for doctor', value: readyCount, color: '#16a34a' },
          { label: 'In progress', value: inProgressCount, color: '#d97706' },
          { label: 'Not started', value: notStartedCount, color: '#aaaaaa' },
          { label: 'Flagged', value: flaggedCount, color: '#ef4444' },
        ].map((s, i) => (
          <div key={i} style={{ background: '#ffffff', border: '0.5px solid #e8e8e8', borderRadius: 10, padding: '12px 14px' }} data-testid={`stat-${i}`}>
            <p style={{ fontSize: 10, color: '#aaaaaa', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 400, margin: 0 }}>{s.label}</p>
            <p style={{ fontSize: 22, fontWeight: 500, color: s.color, margin: '2px 0 0', lineHeight: 1 }}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Urgent Alert Strip */}
      {flaggedPatients.length > 0 && (
        <div className="flex items-center" style={{ background: '#fffbeb', border: '0.5px solid #fde68a', borderRadius: 8, padding: '10px 14px', marginBottom: 16, gap: 10 }} data-testid="alert-strip">
          <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#d97706', flexShrink: 0 }} />
          <p style={{ fontSize: 12, color: '#555555', margin: 0, flex: 1 }}>
            {flaggedPatients.map((p, i) => (
              <span key={p.id}>{i > 0 && ' · '}<span style={{ fontWeight: 500, color: '#111111' }}>{p.name}</span> — {p.flagReason}</span>
            ))}
          </p>
          <button
            className="flex items-center flex-shrink-0"
            style={{ background: '#111111', color: '#ffffff', borderRadius: 6, height: 28, padding: '0 12px', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', gap: 5 }}
            data-testid="batch-upload-btn"
            onClick={() => setBatchOpen(true)}
          >
            <ScanLine style={{ width: 12, height: 12 }} /> Scan all with Lens
          </button>
        </div>
      )}

      <PatientCardList
        patients={patients}
        setPatients={setPatients}
        expandedCard={expandedCard}
        setExpandedCard={setExpandedCard}
        cardTab={cardTab}
        setCardTab={setCardTab}
        syncActions={{ roomPatient: appSync.roomPatient, sendToBilling: appSync.sendToBilling }}
      />

      {/* ━━━ BATCH UPLOAD DIALOG ━━━ */}
      <Dialog open={batchOpen && !batchUploadPatient} onOpenChange={v => { if (!v) setBatchOpen(false); }}>
        <DialogContent className="max-w-lg p-0 gap-0 overflow-hidden" style={{ maxHeight: '85vh' }} aria-describedby={undefined} data-testid="batch-upload-dialog">
          <div style={{ padding: '16px 20px', borderBottom: '0.5px solid #e8e8e8' }}>
            <DialogTitle style={{ fontSize: 16, fontWeight: 500, color: '#111111', margin: 0 }}>
              Batch Scan via Lens
            </DialogTitle>
            <p style={{ fontSize: 12, color: '#888888', marginTop: 2 }}>
              {patientsWithIncompleteTasks.length} patient{patientsWithIncompleteTasks.length !== 1 ? 's' : ''} with {totalIncompleteTasks} remaining task{totalIncompleteTasks !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Progress bar */}
          <div style={{ padding: '14px 20px 0' }}>
            <div className="flex items-center justify-between" style={{ marginBottom: 6 }}>
              <span style={{ fontSize: 11, fontWeight: 500, color: '#111111' }}>Overall prep progress</span>
              <span style={{ fontSize: 11, fontWeight: 500, color: batchProgress === 100 ? '#16a34a' : '#888888' }}>{batchProgress}%</span>
            </div>
            <div style={{ height: 6, background: '#f0f0f0', borderRadius: 3, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${batchProgress}%`, background: batchProgress === 100 ? '#22c55e' : '#111111', borderRadius: 3, transition: 'width 300ms ease' }} />
            </div>
            <p style={{ fontSize: 10, color: '#aaaaaa', marginTop: 4 }}>{totalCompleteTasks} of {totalTasks} tasks completed across all patients</p>
          </div>

          {/* Patient queue */}
          <div style={{ padding: '14px 20px', maxHeight: '50vh', overflowY: 'auto' }}>
            {patientsWithIncompleteTasks.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '32px 0' }}>
                <CheckCircle2 style={{ width: 36, height: 36, color: '#22c55e', margin: '0 auto 10px' }} />
                <p style={{ fontSize: 15, fontWeight: 500, color: '#111111', margin: '0 0 4px' }}>All prep tasks complete</p>
                <p style={{ fontSize: 12, color: '#888888' }}>Every patient is ready for the provider</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {patientsWithIncompleteTasks.map(patient => {
                  const incompleteTasks = patient.tasks.filter(t => !t.done);
                  const completeTasks = patient.tasks.filter(t => t.done);
                  const patientProgress = Math.round((completeTasks.length / patient.tasks.length) * 100);
                  return (
                    <div key={patient.id} style={{ border: '0.5px solid #e8e8e8', borderRadius: 10, padding: '12px 14px', background: '#ffffff' }} data-testid={`batch-patient-${patient.id}`}>
                      <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
                        <div className="flex items-center" style={{ gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#555555' }}>
                            {patient.initials}
                          </div>
                          <div>
                            <p style={{ fontSize: 13, fontWeight: 500, color: '#111111', margin: 0 }}>{patient.name}</p>
                            <p style={{ fontSize: 11, color: '#888888', margin: 0 }}>{patient.time} · {incompleteTasks.length} task{incompleteTasks.length !== 1 ? 's' : ''} remaining</p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleBatchStartPatient(patient)}
                          className="flex items-center"
                          style={{ background: '#6b5ce7', color: '#ffffff', borderRadius: 6, height: 28, padding: '0 10px', fontSize: 11, fontWeight: 500, border: 'none', cursor: 'pointer', gap: 4 }}
                          data-testid={`batch-scan-${patient.id}`}
                        >
                          <ScanLine style={{ width: 12, height: 12 }} /> Scan next
                        </button>
                      </div>
                      {/* Task pills */}
                      <div className="flex flex-wrap" style={{ gap: 4 }}>
                        {patient.tasks.map(task => (
                          <span key={task.key} style={{
                            fontSize: 10, padding: '2px 8px', borderRadius: 12, fontWeight: 500,
                            background: task.done ? '#f0fdf4' : '#f5f3ff',
                            color: task.done ? '#15803d' : '#5b21b6',
                            border: `0.5px solid ${task.done ? '#bbf7d0' : '#ddd6fe'}`,
                          }}>
                            {task.done ? '\u2713' : '\u25CB'} {task.label}
                          </span>
                        ))}
                      </div>
                      {/* Mini progress */}
                      <div style={{ height: 3, background: '#f0f0f0', borderRadius: 2, marginTop: 8, overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${patientProgress}%`, background: patientProgress === 100 ? '#22c55e' : '#6b5ce7', borderRadius: 2, transition: 'width 300ms ease' }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div style={{ padding: '12px 20px', borderTop: '0.5px solid #e8e8e8' }}>
            <button
              onClick={() => setBatchOpen(false)}
              style={{ width: '100%', height: 36, borderRadius: 8, background: patientsWithIncompleteTasks.length === 0 ? '#111111' : '#ffffff', color: patientsWithIncompleteTasks.length === 0 ? '#ffffff' : '#555555', fontSize: 12, fontWeight: 500, border: patientsWithIncompleteTasks.length === 0 ? 'none' : '0.5px solid #e0e0e0', cursor: 'pointer' }}
              data-testid="batch-close-btn"
            >
              {patientsWithIncompleteTasks.length === 0 ? 'Done' : 'Close'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Batch → Single Patient Lens Upload */}
      <DocumentUploadFlow
        open={!!batchUploadPatient}
        onOpenChange={v => { if (!v) { setBatchUploadPatient(null); setBatchUploadTaskKey(null); setBatchUploadTaskType(null); } }}
        patientName={batchUploadPatient?.name || 'Patient'}
        patients={patients}
        mode="single"
        taskType={batchUploadTaskType}
        onComplete={handleBatchDocComplete}
      />
    </div>
  );
};

export default LuminaMADashboard;
